<template>
  <div>
    <!-- Tabela -->
    <div v-if="showTable" class="table-responsive">
      <table :class="tableClass">
        <thead>
          <tr>
            <th v-for="col in columns" :key="col.id || col.campo">
              {{ col.topico || col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="paginatedData.data.length === 0">
            <td :colspan="columns.length" class="text-center">
              {{ noDataMessage }}
            </td>
          </tr>
          <tr v-else v-for="(item, index) in paginatedData.data" :key="index">
            <td v-for="col in columns" :key="col.id || col.campo">
              {{ getCellValue(item, col) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginação -->
    <div :class="{ 'card-footer': !removeFooter }" v-if="hasPages">
      <ul class="pagination pagination-outline">
        <li
          class="page-item previous m-1"
          :class="{ disabled: currentPage == 1 }"
        >
          <span class="page-link" @click="goToPage(currentPage - 1)">
            <i class="previous"></i>
          </span>
        </li>
        <li class="page-item" v-for="page in visiblePages" :key="page">
          <span
            v-if="page !== '...'"
            href="#"
            class="page-link m-1"
            :class="{ active: page == currentPage }"
            @click="goToPage(page)"
            >{{ page }}</span
          >
          <span v-else class="page-link m-1 disabled">...</span>
        </li>
        <li
          class="page-item next m-1"
          :class="{
            disabled: currentPage == totalPages,
          }"
        >
          <span class="page-link" @click="goToPage(currentPage + 1)"
            ><i class="next"></i
          ></span>
        </li>
      </ul>
    </div>
    <div v-if="showBackButton" :class="{ ['card-footer mt-5']: removeFooter }">
      <button class="btn btn-primary" @click.prevent="goBack">Voltar</button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from "vue";
import { useRouter, useRoute } from "vue-router";
import MensagemService from "@/core/services/MensagemService";

interface Column {
  id?: string;
  campo?: string;
  topico: string;
  label?: string;
}

interface PaginatedData<T> {
  data: T[];
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
}

export default defineComponent({
  name: "PaginacaoLista",
  props: {
    items: {
      type: Array as PropType<any[]>,
      required: true,
      default: () => [],
    },
    columns: {
      type: Array as PropType<Column[]>,
      required: true,
      default: () => [],
    },
    perPage: {
      type: Number,
      default: 10,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    backUrl: {
      type: String,
      default: "",
    },
    removeFooter: {
      type: Boolean,
      default: false,
    },
    showEllipsis: {
      type: Boolean,
      default: true,
    },
    maxVisiblePages: {
      type: Number,
      default: 5,
    },
    showTable: {
      type: Boolean,
      default: true,
    },
    tableClass: {
      type: String,
      default: "table table-striped table-bordered",
    },
    noDataMessage: {
      type: String,
      default: "Nenhum dado encontrado.",
    },
  },
  emits: ["update:currentPage", "page-changed", "paginated-data"],
  setup(props, { emit }) {
    const router = useRouter();
    const route = useRoute();

    const internalCurrentPage = ref(props.currentPage);

    const paginatedData = computed<PaginatedData<any>>(() => {
      const startIndex = (internalCurrentPage.value - 1) * props.perPage;
      const endIndex = startIndex + props.perPage;

      const paginatedItems = props.items.slice(startIndex, endIndex);

      return {
        data: paginatedItems,
        total: props.items.length,
        perPage: props.perPage,
        currentPage: internalCurrentPage.value,
        lastPage: Math.ceil(props.items.length / props.perPage),
      };
    });

    const getCellValue = (item: any, col: Column) => {
      // Tenta acessar pelo campo ou id da coluna
      const field = col.campo || col.id;
      return field ? item[field] || "" : "";
    };

    const totalPages = computed(() => paginatedData.value.lastPage);

    const hasPages = computed(() => totalPages.value > 1);

    // Gera as páginas visíveis com ou sem ellipsis
    const visiblePages = computed<(number | string)[]>(() => {
      if (!props.showEllipsis || totalPages.value <= props.maxVisiblePages) {
        return Array.from({ length: totalPages.value }, (_, i) => i + 1);
      }

      const pages: (number | string)[] = [];
      const halfVisible = Math.floor(props.maxVisiblePages / 2);
      let start = internalCurrentPage.value - halfVisible;
      let end = internalCurrentPage.value + halfVisible;

      if (start < 1) {
        start = 1;
        end = props.maxVisiblePages;
      }

      if (end > totalPages.value) {
        end = totalPages.value;
        start = Math.max(1, end - props.maxVisiblePages + 1);
      }

      // Adiciona primeira página e ellipsis se necessário
      if (start > 1) {
        pages.push(1);
        if (start > 2) {
          pages.push("...");
        }
      }

      // Adiciona páginas centrais
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Adiciona ellipsis e última página se necessário
      if (end < totalPages.value) {
        if (end < totalPages.value - 1) {
          pages.push("...");
        }
        pages.push(totalPages.value);
      }

      return pages;
    });

    const goToPage = (page: number | string) => {
      if (page === "..." || typeof page !== "number") return;

      const pageNum = page as number;
      if (
        pageNum < 1 ||
        pageNum > totalPages.value ||
        pageNum === internalCurrentPage.value
      ) {
        return;
      }

      internalCurrentPage.value = pageNum;
      emit("update:currentPage", pageNum);
      emit("page-changed", pageNum);
      emit("paginated-data", paginatedData.value);
    };

    const goBack = () => {
      MensagemService.retorno({
        texto: "Tem certeza que deseja voltar?",
        rota: props.backUrl + (!route?.params?.id ? "" : route?.params?.id),
      });
    };

    watch(
      () => props.currentPage,
      (newPage) => {
        if (newPage !== internalCurrentPage.value) {
          internalCurrentPage.value = newPage;
        }
      }
    );

    watch(
      () => props.items,
      () => {
        // Se os itens mudaram, voltar para a primeira página
        internalCurrentPage.value = 1;
        emit("update:currentPage", 1);
        emit("paginated-data", paginatedData.value);
      }
    );

    // Emite os dados paginados inicialmente
    emit("paginated-data", paginatedData.value);

    return {
      // eslint-disable-next-line vue/no-dupe-keys
      currentPage: internalCurrentPage,
      totalPages,
      hasPages,
      visiblePages,
      goToPage,
      goBack,
      paginatedData,
      getCellValue,
    };
  },
});
</script>

<style scoped>
.card-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}
.page-link:hover {
  cursor: pointer;
}
.table-responsive {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th,
td {
  border: 1px solid #dee2e6;
  padding: 8px;
  text-align: left;
}
th {
  background-color: #f8f9fa;
  font-weight: bold;
}

/* Adicione este estilo para dar recuo na primeira coluna */
th:first-child,
td:first-child {
  padding-left: 1.5rem; /* Ajuste o valor conforme necessário */
}
</style>
